import React from 'react'
import PropTypes from 'prop-types'
import ErrorBoundary from '@components/error/boundary'
import defaults from '@config/defaults'
import { NextSeo } from 'next-seo'
import { CoachesSearchNew } from '@components/coaches-search-new'
import Involvement from '@components/involvement'
import Hero from '@components/hero-new/hero'
import ImproveYourGame from '@components/improve-your-game/improve-your-game'
import Hidden from '@material-ui/core/Hidden'
import Box from '@material-ui/core/Box'
import NoSsr from '@material-ui/core/NoSsr'
import StorySpotlightWithData from '@components/story-spotlight/story-spotlight.query'
import SrOnly from '@components/atoms/sr-only'
import MarketingFormEmbed from '@components/marketing-form-embed'
import TestimonialV2 from '@components/testimonialv2'
import { JsonLd } from '@components/atoms'
import useStyles from './home.styles'
import { FallbackHeroQuery } from './fallback-hero'
import { FEATURED_STORIES_QUERY } from './home.fragment'

const Home = (props) => {
  const {
    hero,
    heroSecondaryCtaText,
    heroSecondaryCtaLink,
    useBackupHero,
    promotedLinksCollection,
    featuredCoachesHeadline,
    featuredCoachesCtaText,
    featuredCoachesCtaLink,
    filteredStoriesHeadline,
    filteredStoriesCategoriesCollection,
    filteredStoriesCtaText,
    filteredStoriesCtaLink,
    testimonialsSection,
    reachHeadline,
    reachCtaText,
    reachCtaLink,
    reachDescription,
    reachSlidesCollection,
    newsletterSignup,
    metaTitle,
    metaDescription,
    metaImage,
    query,
    reqHeaders,
  } = props

  const styles = useStyles()

  const viewerCity = reqHeaders?.['cloudfront-viewer-city'] || ''
  const viewerRegion = reqHeaders?.['cloudfront-viewer-country-region'] || ''

  const area = viewerCity && viewerRegion
    ? `${viewerCity}, ${viewerRegion}`
    : (viewerRegion || viewerCity || 'me')

  return (
    <div className={styles.root}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={metaTitle}
        description={metaDescription}
        canonical={defaults.baseUrl}
        openGraph={{
          title: metaTitle,
          type: 'website',
          url: `${defaults.baseUrl}`,
          images: [{ url: metaImage && metaImage.url }],
          site_name: 'PGA of America',
        }}
      />
      <JsonLd {...{
        '@type': 'Organization',
        '@id': `${defaults.baseUrl}#organization`,
        name: 'PGA of America',
        url: defaults.baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.pga.com/images/logo-blue.png',
        },
        sameAs: [
          'https://www.facebook.com/PGAofAmerica',
          'https://twitter.com/pga',
          'https://www.linkedin.com/company/pga-of-america/',
          'https://www.instagram.com/Pga/',
          'https://www.youtube.com/@PGAofAmericaYT',
          'https://www.tiktok.com/@pgaofamerica',
        ],
      }}
      />
      <JsonLd {...{
        '@type': 'WebSite',
        name: 'PGA of America',
        url: defaults.baseUrl,
      }}
      />
      <JsonLd {...{
        '@type': 'WebPage',
        '@id': `${defaults.baseUrl}#webpage`,
        url: defaults.baseUrl,
        name: 'PGA of America',
        isPartOf: {
          '@id': `${defaults.baseUrl}#website`,
        },
        description: metaDescription,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: defaults.baseUrl,
            },
          ],
        },
        mainEntity: [
          {
            '@id': `${defaults.baseUrl}#itemlist`,
          },
        ],
      }}
      />
      <JsonLd {...{
        '@type': 'ItemList',
        '@id': `${defaults.baseUrl}#itemlist`,
        name: 'Popular Searches',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: `Golf lessons near ${area}`,
            item: `${defaults.baseUrl}coach`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: `Golf clinics near ${area}`,
            item: `${defaults.baseUrl}things-to-do`,
          },
        ],
      }}
      />
      <SrOnly><h1>Homepage of the PGA of America</h1></SrOnly>
      {useBackupHero && (
        <FallbackHeroQuery {...{ query }} />
      )}
      {!useBackupHero && (
        <ErrorBoundary>
          <Hero
            hero={hero}
            ctas={promotedLinksCollection}
            secondaryCtaLink={heroSecondaryCtaLink}
            secondaryCtaText={heroSecondaryCtaText}
          />
        </ErrorBoundary>
      )}
      <NoSsr>
        <ErrorBoundary>
          <Box bgcolor="white" pt={[0, 0, '6.25rem']}>
            <StorySpotlightWithData
              graphqlQuery={FEATURED_STORIES_QUERY}
              parentCollection="homepageCollection"
              query={query}
            />
          </Box>
        </ErrorBoundary>
      </NoSsr>
      <NoSsr>
        <ErrorBoundary>
          <CoachesSearchNew headline={featuredCoachesHeadline} ctaText={featuredCoachesCtaText} ctaLink={featuredCoachesCtaLink} />
        </ErrorBoundary>
      </NoSsr>
      {filteredStoriesCategoriesCollection
          && filteredStoriesCategoriesCollection.items
          && filteredStoriesCategoriesCollection.items.length >= 1 && (
        <ErrorBoundary>
          <ImproveYourGame
            headline={filteredStoriesHeadline}
            ctaText={filteredStoriesCtaText}
            link={filteredStoriesCtaLink}
            categoryList={filteredStoriesCategoriesCollection}
          />
        </ErrorBoundary>
      )}
      {testimonialsSection && testimonialsSection.testimonialEntriesCollection.items.length > 0 && (
        <ErrorBoundary>
          <Hidden smDown>
            <TestimonialV2 testimonialsData={testimonialsSection} />
          </Hidden>
        </ErrorBoundary>
      )}
      {reachSlidesCollection
          && reachSlidesCollection.items
          && reachSlidesCollection.items.length >= 1 && (
        <ErrorBoundary>
          <Involvement
            headline={reachHeadline}
            ctaText={reachCtaText}
            ctaLink={reachCtaLink}
            description={reachDescription}
            slides={reachSlidesCollection}
          />
        </ErrorBoundary>
      )}
      {!!newsletterSignup && (
        <ErrorBoundary>
          <MarketingFormEmbed {...newsletterSignup} />
        </ErrorBoundary>
      )}
    </div>
  )
}

Home.propTypes = {
  hero: PropTypes.object,
  heroSecondaryCtaText: PropTypes.string,
  heroSecondaryCtaLink: PropTypes.string,
  backupHeroSlidesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  backupHeroSpeed: PropTypes.string,
  useBackupHero: PropTypes.bool,
  promotedLinksCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  featuredStoriesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  featuredStoriesHeadline: PropTypes.string,
  featuredStoriesCtaText: PropTypes.string,
  featuredStoriesCtaLink: PropTypes.string,
  featuredCoachesHeadline: PropTypes.string,
  featuredCoachesCtaText: PropTypes.string,
  featuredCoachesCtaLink: PropTypes.string,
  filteredStoriesHeadline: PropTypes.string,
  filteredStoriesCategoriesCollection: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      category: PropTypes.string,
      series: PropTypes.string,
    })),
  }),
  filteredStoriesCtaText: PropTypes.string,
  filteredStoriesCtaLink: PropTypes.string,
  testimonialsSection: PropTypes.shape({
    entryOverline: PropTypes.string,
    testimonialEntriesCollection: PropTypes.shape({
      entryOverline: PropTypes.string,
      testimonialEntriesCollection: PropTypes.shape({
        items: PropTypes.array,
      }),
      backgroundImage: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  reachHeadline: PropTypes.string,
  reachDescription: PropTypes.string,
  reachCtaText: PropTypes.string,
  reachCtaLink: PropTypes.string,
  reachSlidesCollection: PropTypes.shape({
    total: PropTypes.number,
    items: PropTypes.array,
  }),
  newsletterSignup: PropTypes.shape({
    headline: PropTypes.string,
    digiohFormId: PropTypes.string,
  }),
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  metaImage: PropTypes.shape({
    url: PropTypes.string,
  }),
  brandText: PropTypes.string,
  brandImage: PropTypes.shape({
    url: PropTypes.string,
  }),
  brandImageMobile: PropTypes.shape({
    url: PropTypes.string,
  }),
  query: PropTypes.object,
  reqHeaders: PropTypes.object,
}

Home.defaultProps = {}

export default Home
